'use client';

import { useEffect, useRef } from 'react';
import { useTranslation } from '@root/src/i18n/client';
import Link from 'next/link';
import styles from '../header.module.css';
import {
  notifyHeaderDropdownOpen,
  subscribeToHeaderDropdownOpen,
} from '../HeaderDesktop/components/dropdownSync';

const defaultCollectionItems = [
  { name: 'Trà Xanh', slug: 'tra-xanh' },
  { name: 'Trà Ô Long', slug: 'tra-o-long' },
  { name: 'Trà Đen', slug: 'tra-den' },
  { name: 'Trà Thảo Mộc', slug: 'tra-thao-moc' },
  { name: 'Trà Phổ Nhĩ', slug: 'tra-pho-nhi' },
];

interface CollectionDropdownProps {
  collections?: Array<{ slug: string; name?: string; title?: string }>;
}

export default function CollectionDropdown({ collections }: CollectionDropdownProps) {
  const { t } = useTranslation('common');
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const collectionItems = Array.isArray(collections) && collections.length
    ? collections.map(item => ({
        name: item.name || item.title || item.slug,
        slug: item.slug,
      }))
    : defaultCollectionItems;

  useEffect(() => {
    return subscribeToHeaderDropdownOpen('collection', () => {
      if (detailsRef.current?.open) {
        detailsRef.current.open = false;
      }
    });
  }, []);

  useEffect(() => {
    const handlePointerOutside = (event: MouseEvent | TouchEvent) => {
      const dropdownNode = detailsRef.current;
      const target = event.target;

      if (!dropdownNode?.open || !(target instanceof Node)) {
        return;
      }

      if (!dropdownNode.contains(target)) {
        dropdownNode.open = false;
      }
    };

    const handleScrollClose = () => {
      if (detailsRef.current?.open) {
        detailsRef.current.open = false;
      }
    };

    document.addEventListener('mousedown', handlePointerOutside);
    document.addEventListener('touchstart', handlePointerOutside);
    window.addEventListener('scroll', handleScrollClose, true);
    window.addEventListener('wheel', handleScrollClose, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handlePointerOutside);
      document.removeEventListener('touchstart', handlePointerOutside);
      window.removeEventListener('scroll', handleScrollClose, true);
      window.removeEventListener('wheel', handleScrollClose);
    };
  }, []);

  const handleToggle = () => {
    if (detailsRef.current?.open) {
      notifyHeaderDropdownOpen('collection');
    }
  };

  return (
    <details
      ref={detailsRef}
      className={`relative ${styles.collectionDropdown}`}
      onToggle={handleToggle}
    >
      <summary
        className="flex cursor-pointer items-center gap-1 rounded-full px-2 py-1 text-gray-600 transition-colors hover:text-[#223A27]"
      >
        {t('lytra.headerCollections')}
        <span className="text-[10px]">▾</span>
      </summary>

      <div className={`${styles.collectionPanel} absolute left-0 top-full z-50 mt-1 min-w-[220px] overflow-hidden rounded-[24px] border border-[#EDE6D8] bg-white p-3 shadow-[0_18px_45px_rgba(34,58,39,0.08)]`}>
        {collectionItems.map(item => (
          <Link
            key={item.slug}
            href={`/collection/${item.slug}`}
            className="block rounded-2xl px-4 py-2 text-sm text-[#223A27] transition-colors hover:bg-[#F4E8D8]"
            onClick={() => {
              if (detailsRef.current?.open) {
                detailsRef.current.open = false;
              }
            }}
          >
            {item.name}
          </Link>
        ))}
      </div>
    </details>
  );
}
